import { deriveConnectorHealth } from "./connectorHealth.mjs";

/**
 * MER-213: pure rollup logic for the /admin Overview landing page.
 * Plain .mjs so the node:test suite imports it directly (the
 * connectorHealth.mjs precedent). overview-summary.test.mjs pins the
 * thresholds and the plain-English alert copy.
 *
 * Alert philosophy per the ticket's hard rules: plain English a human
 * can act on ("Best Disposal data is 5 days old"), never raw
 * threshold math. Severity is "red" or "amber" only; anything that
 * does not warrant attention emits no alert at all.
 */

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;

/**
 * Roll one customer's entity health rows into card-level numbers.
 *
 * @param {Array<{ entityKey: string, lastSyncAt: string | null,
 *   lastSyncStatus: "success" | "failed" | null }>} entityHealth
 * @param {number} [now]   epoch ms, injectable for deterministic tests.
 * @returns {{
 *   lastSyncAt: string | null,
 *   failedCount: number,
 *   failedEntityKeys: string[],
 *   downConnectors: string[],
 *   freshness: "fresh" | "aging" | "stale" | "none",
 * }}
 */
export function summarizeCustomerHealth(entityHealth, now = Date.now()) {
  let lastSyncAt = null;
  const failedEntityKeys = [];
  for (const row of entityHealth) {
    if (row.lastSyncStatus === "failed") failedEntityKeys.push(row.entityKey);
    if (!row.lastSyncAt) continue;
    const ts = Date.parse(row.lastSyncAt);
    if (!Number.isFinite(ts)) continue;
    if (!lastSyncAt || ts > Date.parse(lastSyncAt)) lastSyncAt = row.lastSyncAt;
  }

  // A connector that synced at least once but not successfully in the
  // last 7 days is "down". Never-synced connectors are "not
  // configured" for this customer and excluded: a customer without
  // Ramp must not alert about Ramp forever.
  const downConnectors = deriveConnectorHealth(entityHealth, now)
    .filter((c) => !c.active && c.lastSyncAt !== null)
    .map((c) => c.name);

  let freshness = "none";
  if (lastSyncAt) {
    const age = now - Date.parse(lastSyncAt);
    freshness = age < ONE_DAY_MS ? "fresh" : age < SEVEN_DAYS_MS ? "aging" : "stale";
  }

  return {
    lastSyncAt,
    failedCount: failedEntityKeys.length,
    failedEntityKeys,
    downConnectors,
    freshness,
  };
}

/** "26 hours" under 2 days, "5 days" from there up. */
function ageCopy(ms) {
  const hours = Math.max(1, Math.round(ms / ONE_HOUR_MS));
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.round(ms / ONE_DAY_MS);
  return `${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Build the Overview alert list across all customers, worst first.
 *
 * @param {Array<{ slug: string, name: string, summary: ReturnType<typeof summarizeCustomerHealth> }>} customers
 * @param {number} [now]
 * @returns {Array<{ severity: "red" | "amber", message: string, slug: string }>}
 */
export function buildAlerts(customers, now = Date.now()) {
  const alerts = [];
  for (const { slug, name, summary } of customers) {
    if (summary.failedCount > 0) {
      const list = summary.failedEntityKeys.map((k) => k.toUpperCase()).join(", ");
      alerts.push({
        severity: "red",
        message: `${name}: ${summary.failedCount} entit${summary.failedCount === 1 ? "y is" : "ies are"} failing to sync (${list})`,
        slug,
      });
    }
    if (summary.lastSyncAt) {
      const age = now - Date.parse(summary.lastSyncAt);
      if (age >= SEVEN_DAYS_MS) {
        alerts.push({ severity: "red", message: `${name} data is ${ageCopy(age)} old`, slug });
      } else if (age >= ONE_DAY_MS) {
        alerts.push({ severity: "amber", message: `${name} data is ${ageCopy(age)} old`, slug });
      }
    } else {
      alerts.push({ severity: "amber", message: `${name} has no synced data yet`, slug });
    }
    // Per-connector alerts only when the customer has otherwise-fresh
    // data: if EVERYTHING is stale the red staleness alert above
    // already says it, and one alert per connector would just repeat
    // it four times.
    if (summary.freshness !== "stale") {
      for (const connector of summary.downConnectors) {
        alerts.push({
          severity: "amber",
          message: `${name}: ${connector} has not synced successfully in 7 days`,
          slug,
        });
      }
    }
  }
  return alerts.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "red" ? -1 : 1));
}
